import {
  alpha,
  Tab as MuiTab,
  Tabs as MuiTabs,
  Stack,
  styled,
  tabClasses,
  tabsClasses,
} from '@mui/material';
import { GLOBAL_TAB_LABEL_CLASSES } from 'src/components/GlobalTabs/tab-label-classes';
import {
  getTabScrollStyles,
  GLOBAL_TABS_INNER_HEIGHT,
} from 'src/components/GlobalTabs/tab-scroll-styles';

export const GLOBAL_TABS_HEIGHT = '3rem';

// The unseen-count badge shares this slot with the close button and, unlike the close button, is
// visible at rest — so unlike the close button it needs a real (unconditional) reserve rather than
// an overlay-only fade, or the id/title would crowd or run under it. The label's own trailing edge
// now sits inside that reserve, so the fade only has to soften its last sliver rather than bridge
// all the way out to the close button.
const TRAILING_FADE = `linear-gradient(to right, #000 calc(100% - 0.1875rem), transparent 100%)`;

export const GlobalTabsBar = styled(Stack)({
  flexDirection: 'row',
  flex: '0 0 auto',
  margin: '0.25rem 0.25rem 0 0',
  padding: '0.25rem 0.375rem 0 0',
});

export const GlobalTabsContainer = styled(MuiTabs)(({ theme }) => {
  return {
    maxHeight: GLOBAL_TABS_INNER_HEIGHT,
    minHeight: GLOBAL_TABS_INNER_HEIGHT,
    height: GLOBAL_TABS_INNER_HEIGHT,
    flex: '1 1 auto',
    minWidth: 0,
    width: 'auto',
    [`& .${tabsClasses.indicator}`]: {
      display: 'none',
    },
    [`& .${tabsClasses.list}`]: {
      alignItems: 'stretch',
      gap: '0.125rem',
    },
    ...getTabScrollStyles(theme),
  };
});

export const Tab = styled(MuiTab)(({ theme }) => {
  const { sys } = theme.palette.custom.OV;

  return {
    position: 'relative',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: '0.375rem',
    textTransform: 'none',
    // The badge/close-button reserve lives on the label's own content box (tab-labels/styled.tsx
    // ContentRoot) rather than here: this padding and the theme's `MuiTab` styleOverrides root
    // padding are two same-specificity rules on the same element, so whichever is inserted later
    // wins the tie — relying on that order to hold to protect the reserve is not safe.
    padding: '0.1875rem 0 0.1875rem 0.6875rem',
    maxHeight: GLOBAL_TABS_INNER_HEIGHT,
    minHeight: GLOBAL_TABS_INNER_HEIGHT,
    height: GLOBAL_TABS_INNER_HEIGHT,
    // Every tab shows its title now, so tabs need a floor wide enough for one to be worth
    // reading and a ceiling so a long subject cannot crowd out its neighbours. Between the two
    // they size to content; past the ceiling the strip scrolls rather than squashing anything.
    minWidth: '8.25rem',
    width: 'auto',
    maxWidth: '11.125rem',
    flexShrink: 0,
    overflow: 'hidden',
    borderRadius: '0.5rem',
    border: `1px solid ${sys.outlineVariant}`,
    backgroundColor: sys.surfaceContainerLow,
    color: sys.onSurface,
    transition: theme.transitions.create(
      ['background-color', 'border-color', 'box-shadow', 'color'],
      { duration: 180 },
    ),

    [`&:not(.${tabClasses.selected}):hover`]: {
      backgroundColor: sys.surfaceContainer,
    },

    // Only while the close button is actually drawn over the label, so a tab at rest keeps a
    // hard-edged ellipsis and pays nothing for a slot it is not using.
    [`&:hover .${GLOBAL_TAB_LABEL_CLASSES.content}`]: {
      WebkitMaskImage: TRAILING_FADE,
      maskImage: TRAILING_FADE,
    },

    [`&.${tabClasses.selected}`]: {
      backgroundColor: sys.surfaceContainerHigh,
      color: sys.onBackground,
      boxShadow: `0 1px 2px ${alpha(sys.shadow, theme.palette.mode === 'dark' ? 0.18 : 0.05)}`,
      [`.${GLOBAL_TAB_LABEL_CLASSES.title}`]: {
        fontWeight: 600,
      },
      [`.${GLOBAL_TAB_LABEL_CLASSES.overline}`]: {
        color: sys.onSurface,
      },
    },

    '.close-tab-button': {
      position: 'absolute',
      top: '50%',
      right: '0.3125rem',
      transform: 'translateY(-50%)',
      width: '1.25rem',
      height: '1.25rem',
      padding: 0,
      borderRadius: '0.25rem',
      color: sys.onSurfaceVariant,
      opacity: 0,
      pointerEvents: 'none',
      transition: theme.transitions.create(['opacity', 'background-color'], {
        duration: 120,
      }),
    },
    '&:hover .close-tab-button': {
      opacity: 1,
      pointerEvents: 'auto',
    },
    [`&:hover .${GLOBAL_TAB_LABEL_CLASSES.unseenBadge}`]: {
      opacity: 0,
    },
    '.close-tab-button:hover': {
      backgroundColor: sys.surfaceContainerHighest,
      color: sys.onBackground,
    },
  };
});
